import { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function ProcForm({
  serialNo,
  formNo,
  title,
  date,
  children,
}: {
  serialNo?: string;
  formNo: string;
  title: string;
  date?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-5">
      {/* Form header banner */}
      <div className="rounded-2xl border-2 border-emerald-700 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">{formNo}</p>
            <h1 className="mt-0.5 text-xl font-bold text-slate-900">{title}</h1>
          </div>
          <div className="text-right text-sm text-slate-500">
            {serialNo && <p className="font-mono font-bold text-slate-800">{serialNo}</p>}
            {date && <p>{new Date(date).toLocaleDateString("en-KE", { day: "2-digit", month: "long", year: "numeric" })}</p>}
            {!date && <p>{new Date().toLocaleDateString("en-KE", { day: "2-digit", month: "long", year: "numeric" })}</p>}
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}

export function ProcSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <h2 className="mb-4 border-b pb-2 font-bold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}

export function ProcField({ label, value, span2 }: { label: string; value: ReactNode; span2?: boolean }) {
  return (
    <div className={span2 ? "sm:col-span-2" : ""}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-800">{value || "—"}</p>
    </div>
  );
}

export function ProcGrid({ children, cols = 3 }: { children: ReactNode; cols?: 2 | 3 | 4 }) {
  return (
    <div className={`grid gap-4 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : cols === 4 ? "lg:grid-cols-4" : ""}`}>
      {children}
    </div>
  );
}

export function ProcError({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <strong>Error:</strong> {message}
    </div>
  );
}

export function ProcLoading({ label = "Loading…" }: { label?: string }) {
  return <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">{label}</div>;
}

// ─── ProcTabs ─────────────────────────────────────────────────────────────────
// Horizontal tab strip for status-based filtering on list pages.

export type TabOption = { label: string; value: string };

export function ProcTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: TabOption[];
  active: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-2xl border bg-slate-50 p-1">
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          className={[
            "rounded-xl px-4 py-2 text-sm font-semibold transition-all",
            active === t.value
              ? "bg-emerald-700 text-white shadow-sm"
              : "text-slate-600 hover:bg-white hover:text-slate-900",
          ].join(" ")}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── ProcPagination ───────────────────────────────────────────────────────────
// Numbered page controls matching the backend pagination response.

export function ProcPagination({
  page,
  totalPages,
  totalElements,
  onPage,
}: {
  page: number;           // 0-based current page (matches backend `number`)
  totalPages: number;
  totalElements: number;
  onPage: (p: number) => void;
}) {
  if (totalPages <= 1 && totalElements === 0) return null;

  // Build page number window: always show first, last, current ±1
  const pages: (number | "…")[] = [];
  const addPage = (p: number) => {
    if (!pages.includes(p)) pages.push(p);
  };

  for (let i = 0; i < totalPages; i++) {
    if (i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 1) {
      addPage(i);
    }
  }

  // Insert ellipsis where gaps exist
  const withEllipsis: (number | "…")[] = [];
  let prev = -1;
  for (const p of pages as number[]) {
    if (prev !== -1 && p - prev > 1) withEllipsis.push("…");
    withEllipsis.push(p);
    prev = p;
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <span className="text-slate-500">
        {totalElements.toLocaleString()} total &mdash; Page {page + 1} of {Math.max(1, totalPages)}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 0}
          className="flex h-8 w-8 items-center justify-center rounded-lg border disabled:opacity-40 hover:bg-slate-50"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {withEllipsis.map((p, i) =>
          p === "…" ? (
            <span key={`ellipsis-${i}`} className="px-1 text-slate-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPage(p as number)}
              className={[
                "flex h-8 min-w-[2rem] items-center justify-center rounded-lg border px-2 text-sm font-semibold transition",
                p === page
                  ? "border-emerald-700 bg-emerald-700 text-white"
                  : "hover:bg-slate-50",
              ].join(" ")}
            >
              {(p as number) + 1}
            </button>
          ),
        )}

        <button
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages - 1}
          className="flex h-8 w-8 items-center justify-center rounded-lg border disabled:opacity-40 hover:bg-slate-50"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
